/**
 * GoogleAuthCallbackPage — landing route after Google OAuth completes.
 *
 * The backend at /api/auth/google/callback set our access/refresh cookies
 * and 302'd here. We re-hydrate the React auth context, fire a `google_signup`
 * (or `login_success`) GA4 event, and route the user to either the
 * questionnaire (new) or their dashboard (returning).
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { trackEvent } from "../lib/tracking";


export default function GoogleAuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const { refresh } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch { /* cookies are httpOnly; refresh failure → push to login */ }
      if (cancelled) return;
      try {
        trackEvent(isNew ? "google_signup" : "login_success", {
          provider: "google",
          location: "google_callback",
        });
        if (isNew) trackEvent("signup_completed", { provider: "google" });
      } catch { /* swallow */ }
      // Routing:
      //   new user → /questionnaire (start assessment immediately)
      //   returning → /dashboard
      navigate(isNew ? "/questionnaire" : "/dashboard", { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-emerald-700">
          <span className="inline-block w-4 h-4 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Finishing your secure sign-in…</span>
        </div>
      </div>
    </div>
  );
}
